// [목적] AI 자연어 보정(folio-refine, C5) 요청/응답 공유 zod 스키마 — BE/FE 공통 계약
// [이유] POST /folios/:id/refine(제안 미리보기) + POST /folios/:id/refine/:proposalId/apply(적용)
//        두 엔드포인트의 요청 바디·응답 shape 을 packages/types 에 두어 apps/api(zod 검증)와
//        apps/web(응답 파싱, 후속) 양쪽이 동일 계약을 공유한다(folio.ts/collection.ts 선례와 동일 원칙).
//        Day 미리보기(before/after)는 folio.ts 의 dayPlanSchema/planItemSchema(영속 엔티티 shape,
//        id 가 uuid 필수)를 재사용하지 않는다 — after 의 신규 항목은 아직 plan_items 행이 없어 uuid 가
//        없기 때문(재사용 시 가짜 uuid 를 발급해야 하는 부자연스러움 회피, ponytail: 새 경량 스키마).
// [사이드 이팩트] apps/api rag 모듈이 이 스키마로 컨트롤러 입력 검증(ZodValidationPipe) + 응답 구성.
// [수정 시 고려사항] refineCitationSchema 필드(addr1/tel/usetime/restDate)는 TourAPI 원문 그대로(CLAUDE §4,
//                   LLM 변형 금지) — BE 가 poi 테이블 값을 가공 없이 그대로 채워야 한다.

import { z } from 'zod';

// ─── 요청 ───────────────────────────────────────────────────────────────────

export const refineScopeSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('day'), dayIndex: z.number().int().min(1).max(60) }),
  z.object({ type: z.literal('item'), itemId: z.string().uuid() }),
]);
export type RefineScope = z.infer<typeof refineScopeSchema>;

export const refineRequestSchema = z.object({
  instruction: z.string().min(1).max(500),
  scope: refineScopeSchema,
});
export type RefineRequest = z.infer<typeof refineRequestSchema>;

// ─── Day 미리보기(before/after) ─────────────────────────────────────────────

/** [목적] 원문 4필드(addr1/tel/usetime/restDate) 포함 citation — CLAUDE §4 무변형 원칙 */
export const refineCitationSchema = z.object({
  contentId: z.string(),
  title: z.string().nullable(),
  addr1: z.string().nullable(),
  tel: z.string().nullable(),
  usetime: z.string().nullable(),
  restDate: z.string().nullable(),
  imageUrl: z.string().nullable(),
});
export type RefineCitation = z.infer<typeof refineCitationSchema>;

export const refineDayItemSchema = z.object({
  visitOrder: z.number().int(),
  citation: refineCitationSchema,
  storyText: z.string().nullable(),
});
export type RefineDayItem = z.infer<typeof refineDayItemSchema>;

export const refineDaySnapshotSchema = z.object({
  dayIndex: z.number().int(),
  summary: z.string().nullable(),
  items: z.array(refineDayItemSchema),
});
export type RefineDaySnapshot = z.infer<typeof refineDaySnapshotSchema>;

// ─── 응답 ───────────────────────────────────────────────────────────────────

export const refineProposalResponseSchema = z.object({
  proposalId: z.string(),
  day: z.object({
    before: refineDaySnapshotSchema,
    after: refineDaySnapshotSchema,
  }),
  /** 사용자에게 보여줄 변경 요약 문구 목록(예: "카페 제거", "바다 추가") — LLM 생성, 자유 문장 */
  changes: z.array(z.string()),
});
export type RefineProposalResponse = z.infer<typeof refineProposalResponseSchema>;

export const refineApplyResponseSchema = z.object({
  status: z.literal('applied'),
  portfolioId: z.string(),
  dayIndex: z.number().int(),
});
export type RefineApplyResponse = z.infer<typeof refineApplyResponseSchema>;
